import { useQueries, type UseQueryOptions } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { useMemo, useRef } from 'react';

export type PageData<TItem> = {
  data: TItem[];
  totalCount: number;
};

type P<TItem, TQueryData extends PageData<TItem>, TError = Error> = {
  readonly debounceMs?: number;
  readonly getPageQueryOptions: (
    offset: number,
    limit: number,
  ) => UseQueryOptions<TQueryData, TError, TQueryData>;
  readonly pageSize: number;
  readonly visibleItemRange: { startIndex: number; endIndex: number };
};

/**
 * Generic sparse-paginated data fetching hook.
 *
 * Always keeps page 0 loaded (so `totalCount` is available immediately), then
 * subscribes to the pages that overlap with the (debounced) visible item
 * range. The visible range is debounced to coalesce fetches during fast
 * scrolling.
 *
 * The returned `itemsMap` is **sticky**: once an item has been loaded under
 * the current query factory, it stays in the map until the factory identity
 * changes (e.g. filters change). This prevents the "skeleton flash" that
 * would otherwise appear during the debounce window when scrolling back over
 * pages we've already loaded — the data is in TanStack Query's cache but
 * `useQueries` is not subscribed to those pages right that moment.
 *
 * Designed to be paired with `useVirtualizer` from @tanstack/react-virtual.
 */
export function useSparsePaginatedQuery<
  TItem,
  TQueryData extends PageData<TItem>,
  TError = Error,
>({
  debounceMs = 50,
  getPageQueryOptions,
  pageSize,
  visibleItemRange,
}: P<TItem, TQueryData, TError>) {
  const debouncedRange = useDebounce(visibleItemRange, debounceMs);

  const pageIndices = useMemo(() => {
    const pages = new Set<number>([0]);

    const startPage = Math.floor(debouncedRange.startIndex / pageSize);
    const endPage = Math.floor(debouncedRange.endIndex / pageSize);

    for (let i = startPage; i <= endPage; i++) {
      pages.add(i);
    }

    return Array.from(pages).sort((a, b) => a - b);
  }, [debouncedRange, pageSize]);

  // `combine` derives the result from the queries array inside TanStack Query
  // and applies `replaceEqualDeep` to keep the returned value referentially
  // stable when nothing has changed. This avoids feeding the unstable
  // `useQueries` return value into a `useMemo` dependency array.
  const liveResult = useQueries({
    combine: (results) => {
      const itemsMap = new Map<number, TItem>();

      results.forEach((result, queryArrayIndex) => {
        if (result.data) {
          const pageIndex = pageIndices[queryArrayIndex] ?? 0;

          result.data.data.forEach((item, itemIndex) => {
            itemsMap.set(pageIndex * pageSize + itemIndex, item);
          });
        }
      });

      const totalCount =
        results.find((r) => r.data !== undefined)?.data?.totalCount ?? 0;
      const hasInitialData = results.some((r) => r.data !== undefined);
      const isInitialLoading =
        !hasInitialData && results.some((r) => r.isLoading);
      const isLoadingMorePages =
        hasInitialData && results.some((r) => r.isLoading);

      return {
        isInitialLoading,
        isLoadingMorePages,
        itemsMap,
        totalCount,
      };
    },
    queries: pageIndices.map((pageIndex) =>
      getPageQueryOptions(pageIndex * pageSize, pageSize),
    ),
  });

  // Sticky items: persist items across renders for as long as the factory
  // identity is stable (i.e. filters / inputs unchanged).
  // Reset on any change so we never show items from a previous filter set.
  const stickyMapRef = useRef(new Map<number, TItem>());
  const factoryRef = useRef(getPageQueryOptions);

  if (factoryRef.current !== getPageQueryOptions) {
    factoryRef.current = getPageQueryOptions;
    stickyMapRef.current = new Map();
  }

  for (const [index, item] of liveResult.itemsMap) {
    stickyMapRef.current.set(index, item);
  }

  return {
    isInitialLoading: liveResult.isInitialLoading,
    isLoadingMorePages: liveResult.isLoadingMorePages,
    itemsMap: stickyMapRef.current,
    totalCount: liveResult.totalCount,
  };
}
